import { Fragment, useEffect, useState } from 'react';
import { Users, RefreshCw, AlertTriangle, CheckCircle2, ArrowDownCircle, ArrowUpCircle, Settings, Plus, Minus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { ConfigPontoModal } from './ConfigPontoModal';
import { PontoSemanasDoMes } from './ponto-batidas';
import { ModalLancarBatidaPonto } from './ModalLancarBatidaPonto';

interface EventoHoje { id: number; tipo: string; hora: string; bypass: boolean; auto: boolean }

interface MembroEquipe {
  id: number;
  nome: string;
  perfil: string;
  ativo: boolean;
  controle_acesso_ativo: boolean;
  hora_inicio: string | null;
  hora_fim: string | null;
  almoco_inicio: string | null;
  almoco_fim: string | null;
  dias_semana: number[];
  sab_hora_inicio: string | null;
  sab_hora_fim: string | null;
  rostos_cadastrados: number;
  entrada_hoje: string | null;
  saida_hoje: string | null;
  atrasou_hoje: boolean | null;
  bypass_hoje: boolean | null;
  eventos_hoje: EventoHoje[] | null;
  atrasos_semana: number;
  anomalias_semana: number;
  atraso_minutos_semana: number;
}

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

function fmtHora(d: string | null): string {
  if (!d) return '-';
  return new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function fmtTime(t: string | null): string {
  if (!t) return '-';
  return String(t).slice(0, 5);
}

// Minutos desde a meia-noite, agora. A tela do Ruan e a VPS rodam no mesmo fuso (America/
// Sao_Paulo), entao aqui basta o relogio local — e' so pra decidir o PESO do botao, nunca a hora
// que vai ser gravada (essa vem do servidor, no contexto do modal).
function agoraEmMinutos(): number {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

function horaCadastroEmMinutos(t: string | null): number | null {
  if (!t) return null;
  const [h, m] = String(t).slice(0, 5).split(':').map(Number);
  if (h === undefined || Number.isNaN(h)) return null;
  return h * 60 + (m || 0);
}

// "Esta pessoa esta com o ponto ABERTO e o expediente dela ja acabou" — o caso que gerou o
// recurso ([DA-690]): o scheduler que fecha o ponto esquecido so roda as 23h, entao quem tem
// horario ate as 11h fica 12 horas dentro do rodizio de chamados sem estar na loja.
// ⚠️ Sem `hora_fim` cadastrado nao ha "fora de hora" que se possa afirmar — devolve false.
function pontoForaDeHora(m: MembroEquipe): boolean {
  const eventos = m.eventos_hoje || [];
  const ultimo = eventos[eventos.length - 1];
  if (!ultimo || ultimo.tipo !== 'entrada') return false;
  const ehSabado = new Date().getDay() === 6;
  const fim = horaCadastroEmMinutos((ehSabado && m.sab_hora_fim) ? m.sab_hora_fim : m.hora_fim);
  return fim !== null && agoraEmMinutos() > fim;
}

export function Equipe() {
  const { usuario } = useAuth();
  const toast = useToast();
  const isAdmin = ['admin', 'gerente'].includes(usuario?.perfil || '');
  const isSomenteAdmin = usuario?.perfil === 'admin';

  const [membros, setMembros] = useState<MembroEquipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [configAberto, setConfigAberto] = useState(false);
  const [soPontoAtivo, setSoPontoAtivo] = useState(false);
  // Quem saiu da loja (DA-397). Esta tela e a unica porta pro historico de ponto de alguem, entao
  // sem isso inativar o cadastro tornava o registro dele inalcancavel. Persiste como os demais
  // filtros de tela (principio #6 / `filtros_tela_localStorage`).
  const [mostrarInativos, setMostrarInativos] = useState(() => localStorage.getItem('equipe.mostrarInativos') === '1');
  useEffect(() => { localStorage.setItem('equipe.mostrarInativos', mostrarInativos ? '1' : '0'); }, [mostrarInativos]);
  // Teto da regua de minutos (0 = desligado) — usado so pra pintar o aviso na coluna.
  const [tetoMin, setTetoMin] = useState(0);
  // Linhas com o "+" do ponto aberto (so admin/gerente) — mostra as semanas do mes.
  const [pontoAberto, setPontoAberto] = useState<Set<number>>(new Set());
  // Quem vai receber a batida lancada a mao ([DA-690]). null = modal fechado.
  const [lancarPara, setLancarPara] = useState<{ id: number; nome: string } | null>(null);

  function togglePonto(id: number) {
    setPontoAberto((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function carregar() {
    setLoading(true);
    try {
      const r = await api.get<MembroEquipe[]>('/api/funcionarios/equipe', mostrarInativos ? { query: { inativos: 1 } } : undefined);
      setMembros(r);
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function carregarTeto() {
    try {
      const r = await api.get<{ valor: string | null }>('/api/app-settings/ponto_total_atraso_semana_min');
      setTetoMin(parseInt(r.valor ?? '0', 10) || 0);
    } catch { /* teto fica 0 (aviso oculto) */ }
  }

  useEffect(() => { void carregarTeto(); }, []);
  // Recarrega quando o filtro de inativos muda (o recorte e do BACKEND, nao do cliente).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void carregar(); }, [mostrarInativos]);

  const visiveis = soPontoAtivo ? membros.filter((m) => m.controle_acesso_ativo) : membros;

  return (
    <div>
      <div className="pagina-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="pagina-titulo">
          <Users size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Equipe
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {isSomenteAdmin && (
            <button type="button" className="btn-secundario" onClick={() => setConfigAberto(true)} title="Tolerancia, pausa irreal e teto de atraso da semana">
              <Settings size={14} /> Config. do ponto
            </button>
          )}
          <button type="button" className="btn-secundario" onClick={() => void carregar()}>
            <RefreshCw size={14} /> Atualizar
          </button>
        </div>
      </div>

      {isSomenteAdmin && (
        <ConfigPontoModal aberto={configAberto} onFechar={() => { setConfigAberto(false); void carregarTeto(); }} />
      )}

      <ModalLancarBatidaPonto
        aberto={!!lancarPara}
        usuarioId={lancarPara?.id ?? null}
        usuarioNome={lancarPara?.nome ?? ''}
        onFechar={() => setLancarPara(null)}
        onGravado={() => void carregar()}
      />

      {!loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 12px 2px' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={soPontoAtivo} onChange={(e) => setSoPontoAtivo(e.target.checked)} />
            Só com ponto ativo
          </label>
          {isAdmin && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
              Mostrar inativos
            </label>
          )}
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{visiveis.length} colaborador{visiveis.length === 1 ? '' : 'es'}</span>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : (
        <table className="tabela" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Nome</th>
              <th style={{ width: 110 }}>Perfil</th>
              <th style={{ width: 130 }}>Horario</th>
              <th style={{ width: 110 }}>Dias</th>
              <th style={{ width: 80, textAlign: 'center' }} title="Rostos cadastrados pra FR">FR</th>
              <th style={{ width: 200 }}>Ponto hoje</th>
              {isAdmin && <th style={{ width: 100, textAlign: 'center' }} title="Atrasos+anomalias na semana">Avisos sem.</th>}
            </tr>
          </thead>
          <tbody>
            {visiveis.length === 0 ? (
              <tr><td colSpan={isAdmin ? 7 : 6} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>{soPontoAtivo ? 'Nenhum colaborador com ponto ativo' : 'Sem usuarios ativos'}</td></tr>
            ) : visiveis.map((m) => {
              const horarioStr = m.hora_inicio && m.hora_fim ? `${fmtTime(m.hora_inicio)}–${fmtTime(m.hora_fim)}` : '-';
              const sabStr = m.sab_hora_inicio && m.sab_hora_fim ? `Sab ${fmtTime(m.sab_hora_inicio)}–${fmtTime(m.sab_hora_fim)}` : '';
              const dias = (m.dias_semana || []).map((d) => DIAS[d]).join(', ');
              const eventos = m.eventos_hoje || [];
              const avisos = (m.atrasos_semana || 0) + (m.anomalias_semana || 0);
              const corAvisos = avisos >= 2 ? '#dc2626' : avisos >= 1 ? '#d97706' : '#10b981';

              const pontoOpen = pontoAberto.has(m.id);
              const ultimoEvento = eventos[eventos.length - 1];
              const pontoEmAberto = !!ultimoEvento && ultimoEvento.tipo === 'entrada';
              const foraDeHora = pontoForaDeHora(m);

              return (
                <Fragment key={m.id}>
                <tr>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => togglePonto(m.id)}
                          title={pontoOpen ? 'Ocultar ponto por semana' : 'Ver ponto por semana'}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 22, height: 22, borderRadius: 5, flexShrink: 0, cursor: 'pointer',
                            border: '1px solid var(--input-border, #d1d5db)', background: 'transparent', color: 'var(--text-muted)',
                          }}
                        >
                          {pontoOpen ? <Minus size={13} /> : <Plus size={13} />}
                        </button>
                      )}
                      <span>{m.nome}</span>
                      {/* Cinza: "saiu da loja" e fato, nao pendencia (principio #3). */}
                      {m.ativo === false && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>(inativo)</span>
                      )}
                    </div>
                    {!m.controle_acesso_ativo && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: isAdmin ? 30 : 0 }}>FR desativado</div>
                    )}
                  </td>
                  <td>{m.perfil}</td>
                  <td>
                    <div style={{ fontSize: 13 }}>{horarioStr}</div>
                    {sabStr && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sabStr}</div>}
                  </td>
                  <td style={{ fontSize: 12 }}>{dias || '-'}</td>
                  <td style={{ textAlign: 'center' }}>
                    {m.rostos_cadastrados > 0 ? (
                      <span style={{ color: m.rostos_cadastrados >= 5 ? '#10b981' : '#d97706', fontWeight: 600 }}>{m.rostos_cadastrados}</span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    )}
                  </td>
                  <td>
                    {eventos.length === 0 ? (
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>sem batidas</span>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, fontSize: 12 }}>
                        {eventos.map((e) => {
                          const Icon = e.tipo === 'entrada' ? ArrowDownCircle : ArrowUpCircle;
                          const cor = e.tipo === 'entrada' ? '#16a34a' : '#ea580c';
                          return (
                            <span key={e.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: cor }} title={`${e.tipo}${e.bypass ? ' (bypass)' : ''}${e.auto ? ' (auto)' : ''}`}>
                              <Icon size={11} /> {fmtHora(e.hora)}{e.bypass ? '*' : ''}{e.auto ? '~' : ''}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {m.atrasou_hoje && (
                      <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>atrasou</div>
                    )}
                    {/* Fechar o ponto de quem esqueceu ([DA-690]). Acao POR LINHA de proposito —
                        nada de marcar varios e "aplicar", que faria o default decidir por ele.
                        Dois pesos: quem ja passou do horario ganha o selo e o botao cheio; quem
                        ainda esta no expediente ganha so o botao discreto (existe se precisar,
                        nao grita). O par #fde68a/#000 e fixo, nunca variavel de tema — selo que
                        pinta fundo E fonte com token nao garante contraste entre eles (DA-678). */}
                    {isAdmin && pontoEmAberto && (
                      <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {foraDeHora && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#fde68a', color: '#000' }}>
                            aberto desde {fmtHora(eventos[eventos.length - 1]!.hora)} · devia sair {fmtTime((new Date().getDay() === 6 && m.sab_hora_fim) ? m.sab_hora_fim : m.hora_fim)}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => setLancarPara({ id: m.id, nome: m.nome })}
                          title={foraDeHora
                            ? `Fechar o ponto de ${m.nome} — ela sai do rodizio e os chamados nao respondidos voltam pra fila`
                            : `Fechar o ponto de ${m.nome} (ainda esta dentro do horario dela)`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: foraDeHora ? '3px 9px' : '2px 7px',
                            borderRadius: 5, fontSize: 11, cursor: 'pointer',
                            fontWeight: foraDeHora ? 700 : 400,
                            background: foraDeHora ? '#fde68a' : 'transparent',
                            color: foraDeHora ? '#000' : 'var(--text-muted)',
                            border: `1px solid ${foraDeHora ? '#f59e0b' : 'var(--input-border, #d1d5db)'}`,
                          }}
                        >
                          <ArrowUpCircle size={11} /> Fechar ponto
                        </button>
                      </div>
                    )}
                  </td>
                  {isAdmin && (
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: corAvisos, fontWeight: 600 }}>
                        {avisos === 0 ? <CheckCircle2 size={14} style={{ display: 'inline' }} /> : (
                          <>
                            <AlertTriangle size={12} style={{ display: 'inline', marginRight: 2 }} />
                            {avisos}
                          </>
                        )}
                      </span>
                      {avisos > 0 && (
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                          {m.atrasos_semana} atr · {m.anomalias_semana} anom
                        </div>
                      )}
                      {tetoMin > 0 && (m.atraso_minutos_semana || 0) > 0 && (
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            marginTop: 2,
                            color: (m.atraso_minutos_semana || 0) >= tetoMin ? '#dc2626' : 'var(--text-muted)',
                          }}
                          title={`Minutos de atraso somados na semana (teto ${tetoMin})`}
                        >
                          {m.atraso_minutos_semana} min/sem{(m.atraso_minutos_semana || 0) >= tetoMin ? ` (estourou ${tetoMin})` : ''}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
                {isAdmin && pontoOpen && (
                  <tr>
                    <td colSpan={7} style={{ padding: '4px 12px 14px', background: 'var(--card-bg, #f9fafb)' }}>
                      <PontoSemanasDoMes usuarioId={m.id} />
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
